<template>
  <div class="flex flex-col md:flex-row bg-white rounded-lg overflow-hidden min-h-[300px]">
    <aside ref="asideRef" class="w-full md:w-64 bg-base-200 border border-base-300 p-4 md:p-6 rounded-t-lg md:rounded-l-lg md:rounded-tr-none">
      <ul class="menu space-y-2" role="tablist" aria-orientation="vertical">
        <li>
          <button
            type="button"
            :class="[
              'w-full flex items-center space-x-3 px-4 py-3 text-left text-base md:text-lg font-medium rounded transition-colors',
              selectedTab === 'moderation' ? 'bg-white text-primary shadow ring-1 ring-primary/20' : 'text-gray-700 hover:text-gray-900 hover:bg-base-100'
            ]"
            role="tab"
            aria-selected="true"
            aria-controls="security-moderation"
            @click="selectedTab = 'moderation'"
          >
            <Icon icon="heroicons-outline:shield-check" class="w-5 h-5 md:w-6 md:h-6" />
            <span>{{ t('aiChatAgentSetup.security.moderationTitle', 'Moderation') }}</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            :class="[
              'w-full flex items-center space-x-3 px-4 py-3 text-left text-base md:text-lg font-medium rounded transition-colors',
              selectedTab === 'pii' ? 'bg-white text-primary shadow ring-1 ring-primary/20' : 'text-gray-700 hover:text-gray-900 hover:bg-base-100'
            ]"
            role="tab"
            aria-selected="false"
            aria-controls="security-pii"
            @click="selectedTab = 'pii'"
          >
            <Icon icon="heroicons-outline:key" class="w-5 h-5 md:w-6 md:h-6" />
            <span>{{ t('aiChatAgentSetup.security.piiTitle', 'PII') }}</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            :class="[
              'w-full flex items-center space-x-3 px-4 py-3 text-left text-base md:text-lg font-medium rounded transition-colors',
              selectedTab === 'guardrails' ? 'bg-white text-primary shadow ring-1 ring-primary/20' : 'text-gray-700 hover:text-gray-900 hover:bg-base-100'
            ]"
            role="tab"
            aria-selected="false"
            aria-controls="security-guardrails"
            @click="selectedTab = 'guardrails'"
          >
            <Icon icon="heroicons-outline:shield-exclamation" class="w-5 h-5 md:w-6 md:h-6" />
            <span>{{ t('aiChatAgentSetup.security.guardrailsTitle', 'Guardrails') }}</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            :class="[
              'w-full flex items-center space-x-3 px-4 py-3 text-left text-base md:text-lg font-medium rounded transition-colors',
              selectedTab === 'limits' ? 'bg-white text-primary shadow ring-1 ring-primary/20' : 'text-gray-700 hover:text-gray-900 hover:bg-base-100'
            ]"
            role="tab"
            aria-selected="false"
            aria-controls="security-limits"
            @click="selectedTab = 'limits'"
          >
            <Icon icon="heroicons-outline:adjustments-horizontal" class="w-5 h-5 md:w-6 md:h-6" />
            <span>{{ t('aiChatAgentSetup.security.limitsTitle', 'Limits') }}</span>
          </button>
        </li>
      </ul>
    </aside>
    <div class="flex-1 p-4 md:p-6 bg-white">
      <section
        id="security-moderation"
        v-show="selectedTab === 'moderation'"
        role="tabpanel"
        class="p-4 md:p-6 bg-base-100 border border-base-300 rounded-box"
        :style="{ minHeight: contentMinHeight }"
      >
        <h2 class="text-lg md:text-xl font-semibold mb-2">{{ t('aiChatAgentSetup.security.moderationTitle', 'Moderation') }}</h2>
        <p class="text-sm text-gray-500 mb-4">{{ t('aiChatAgentSetup.advanced.contentModerationDescription') }}</p>
        <div class="form-control w-full max-w-2xl">
          <div class="flex items-center justify-end mb-2">
            <label class="sr-only">Language</label>
            <select v-model="selectedModerationLang" class="select select-bordered select-sm" aria-label="Language">
              <option v-for="lang in languages" :key="lang.code" :value="lang.code">{{ lang.name }}</option>
            </select>
          </div>
          <textarea
            v-model="currentModerationMessage"
            class="textarea textarea-bordered w-full"
            style="min-height:120px;"
            :placeholder="t('aiChatAgentSetup.advanced.contentModerationPlaceholder', 'Enter the moderation response shown when content is inappropriate')"
            aria-label="Inappropriate content message"
          ></textarea>
          <div class="mt-1 text-xs text-gray-400 text-right">{{ moderationChars }} {{ t('reports.charsSuffix', 'chars') }}</div>
        </div>
      </section>

      <section
        id="security-pii"
        v-show="selectedTab === 'pii'"
        role="tabpanel"
        class="p-4 md:p-6 bg-base-100 border border-base-300 rounded-box"
        :style="{ minHeight: contentMinHeight }"
      >
        <h2 class="text-lg md:text-xl font-semibold mb-2">{{ t('aiChatAgentSetup.security.piiTitle', 'PII') }}</h2>
        <div class="grid max-w-2xl grid-space-4">
          <div class="form-control mt-2">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="effectiveEnablePIIRedaction === true" @change="onEnablePIIRedactionChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Enable PII redaction" :disabled="disablePIIRedactionToggle === true" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.redactPrivateInformation', 'Redact private information') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.redactHint', 'Redacts credit cards, SSNs and similar patterns') }}</div>
            <div v-if="disablePIIRedactionToggle === true" class="text-xs text-warning mt-1">{{ piiLockedMessage }}</div>
          </div>
          <div class="form-control mt-2">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="props.redactPhoneNumbers === true" @change="onRedactPhoneNumbersChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Also redact phone numbers" :disabled="effectiveEnablePIIRedaction !== true" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.redactPhoneNumbersLabel', 'Also redact phone numbers') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.redactPhoneNumbersTooltip.description', 'When PII redaction is enabled, also mask phone numbers. Names are never redacted.') }}</div>
          </div>
          <div class="form-control mt-2">
            <label class="cursor-pointer label justify-start">
              <input type="checkbox" :checked="props.redactEmails === true" @change="onRedactEmailsChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Also redact email addresses" :disabled="effectiveEnablePIIRedaction !== true" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.redactEmailsLabel', 'Also redact email addresses') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.redactEmailsTooltip.description', 'When PII redaction is enabled, also mask email addresses.') }}</div>
          </div>
        </div>
      </section>

      <section
        id="security-guardrails"
        v-show="selectedTab === 'guardrails'"
        role="tabpanel"
        class="p-4 md:p-6 bg-base-100 border border-base-300 rounded-box"
        :style="{ minHeight: contentMinHeight }"
      >
        <h2 class="text-lg md:text-xl font-semibold mb-2">{{ t('aiChatAgentSetup.security.guardrailsTitle', 'Guardrails') }}</h2>
        <div class="grid max-w-2xl grid-space-4">
          <div class="form-control mt-2">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="verifyLinksLocal === true" @change="onVerifyLinksChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Verify links in answers" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.verifyLinks', 'Verify links in answers') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.verifyLinksHint', 'Only include links that exist in the knowledge base') }}</div>
          </div>

          <div v-if="disableVerifyAnswerToggle !== true" class="form-control mt-2">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="verifyAnswerEnabledLocal === true" @change="onVerifyAnswerEnabledChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Verify Answer Twice" :disabled="disableVerifyAnswerToggle === true" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.verifyAnswerTwice', 'Verify Answer Twice') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.verifyAnswerTwiceHint', 'Run a second pass to verify and correct the answer using the knowledge base before replying.') }}</div>
          </div>

          <div class="divider my-4"></div>

          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.customBlockRegex', 'Custom block regex (one per line)') }}</span>
            </label>
            <textarea
              :value="customBlockRegexText"
              @input="onCustomBlockRegexInput($event.target.value)"
              class="textarea textarea-bordered"
              style="min-height:120px;"
              :placeholder="t('aiChatAgentSetup.advanced.customBlockRegexPlaceholder', 'e.g. ^.*secret.*$')"
              aria-label="Custom block regex"
            ></textarea>
          </div>

          <div class="divider my-4"></div>

          <div class="form-control w-full">
            <div class="flex items-center justify-between">
              <span>{{ t('aiChatAgentSetup.security.auth.require', 'Require authentication') }}</span>
              <input
                type="checkbox"
                :checked="authenticatorEnabledLocal"
                @change="onAuthenticatorEnabledChange($event.target.checked)"
                class="toggle toggle-primary"
                aria-label="Require authentication"
              />
            </div>
          </div>
          <div class="form-control w-full">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.security.auth.provider', 'Provider') }}</span>
            </label>
            <select class="select select-bordered" :value="authenticatorProviderLocal" @change="onAuthenticatorProviderChange($event.target.value)">
              <option value="google">Google</option>
              <option value="aad">Azure Active Directory</option>
            </select>
          </div>
          <div v-if="authStatusLoading === true" class="alert alert-info shadow-sm">
            <span>{{ t('aiChatAgentSetup.security.auth.checking', 'Checking provider configuration...') }}</span>
          </div>
          <div v-else-if="authNotConfiguredText !== ''" class="alert alert-warning shadow-sm">
            <span>{{ authNotConfiguredText }}</span>
          </div>
          <div class="form-control w-full">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.security.auth.starting', 'Starting message template') }}</span>
            </label>
            <textarea
              v-model="authenticatorWelcomeMessageModel"
              class="textarea textarea-bordered w-full"
              style="min-height:100px;"
              placeholder="Welcome! Please sign in at {{authURL}} to continue."
              aria-label="Authentication welcome message"
            ></textarea>
            <div class="text-xs text-right text-gray-500 mt-1" v-pre>{{ t('aiChatAgentSetup.security.auth.usePlaceholder','Use {{authURL}} inside the message.') }}</div>
          </div>
          <div class="form-control w-full">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.security.auth.loginRequest', 'Login request template') }}</span>
            </label>
            <textarea
              v-model="authenticatorRetryMessageModel"
              class="textarea textarea-bordered w-full"
              style="min-height:100px;"
              placeholder="You must login via {{authURL}} to continue."
              aria-label="Authentication retry message"
            ></textarea>
            <div class="text-xs text-right text-gray-500 mt-1">{{ t('aiChatAgentSetup.security.auth.retryHint', 'Template applied when login is required.') }}</div>
          </div>
          <div class="form-control w-full">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.security.auth.success', 'Success message template') }}</span>
            </label>
            <textarea
              v-model="authenticatorSuccessMessageModel"
              class="textarea textarea-bordered w-full"
              style="min-height:100px;"
              placeholder="You are signed in. Lets talk."
              aria-label="Authentication success message"
            ></textarea>
            <div class="text-xs text-right text-gray-500 mt-1">{{ t('aiChatAgentSetup.security.auth.successHint', 'Sent after successful login.') }}</div>
          </div>
          <div class="form-control w-full">
            <div class="flex items-center justify-between">
              <span>{{ t('aiChatAgentSetup.security.auth.runTool', 'Trigger AI tool after login') }}</span>
              <input
                type="checkbox"
                :checked="authenticatorRunAiToolOnLoginLocal"
                @change="onAuthenticatorRunToolChange($event.target.checked)"
                class="toggle toggle-primary"
                aria-label="Run AI tool after login"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        id="security-limits"
        v-show="selectedTab === 'limits'"
        role="tabpanel"
        class="p-4 md:p-6 bg-base-100 border border-base-300 rounded-box"
        :style="{ minHeight: contentMinHeight }"
      >
        <h2 class="text-lg md:text-xl font-semibold mb-2">{{ t('aiChatAgentSetup.security.limitsTitle', 'Limits') }}</h2>
        <div class="grid max-w-2xl grid-space-4">
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitReachedMessageLabel', 'Message when limit reached') }}</span>
            </label>
            <textarea
              v-model="limitReachedMessageModel"
              class="textarea textarea-bordered"
              style="min-height:100px;"
              :placeholder="t('aiChatAgentSetup.advanced.limitReachedMessagePlaceholder', 'You have reached the maximum messages for this conversation. Start a new chat to continue.')"
              aria-label="Limit reached message"
            ></textarea>
          </div>
          <div class="form-control">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="limitMessagesPerConversationEnabledLocal === true" @change="onLimitMessagesPerConversationEnabledChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Enable limit per conversation" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.enableLimitPerConversation', 'Limit messages per conversation') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.limitPerConversationHint', 'When off, unlimited') }}</div>
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitMessagesPerConversation', 'Limit messages per conversation') }}</span>
            </label>
            <input
              type="number"
              min="0"
              :value="messageLimitPerConversationLocal"
              @input="onMessageLimitPerConversationInput($event.target.value)"
              class="input input-bordered"
              :placeholder="t('aiChatAgentSetup.advanced.unlimitedPlaceholder', '0 for unlimited')"
              :disabled="limitMessagesPerConversationEnabledLocal !== true"
              aria-label="Limit messages per conversation"
            />
          </div>
          <div class="form-control">
            <label class="cursor-pointer label justify-start space-x-3">
              <input type="checkbox" :checked="limitMessagesPerIPEnabledLocal === true" @change="onLimitMessagesPerIPEnabledChange($event.target.checked)" class="toggle toggle-primary mr-2" aria-label="Enable limit per IP" />
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.enableLimitPerIP', 'Limit messages per IP') }}</span>
            </label>
            <div class="text-xs text-gray-500 mt-1">{{ t('aiChatAgentSetup.advanced.limitPerIPHint', 'When off, unlimited') }}</div>
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitMessagesPerIP', 'Limit messages per IP') }}</span>
            </label>
            <input
              type="number"
              min="0"
              :value="messageLimitPerIPLocal"
              @input="onMessageLimitPerIPInput($event.target.value)"
              class="input input-bordered"
              :placeholder="t('aiChatAgentSetup.advanced.unlimitedPlaceholder', '0 for unlimited')"
              :disabled="limitMessagesPerIPEnabledLocal !== true"
              aria-label="Limit messages per IP"
            />
          </div>
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitMessagesPerIPDurationMinutes', 'IP limit duration in minutes') }}</span>
            </label>
            <input
              type="number"
              min="1"
              :value="messageLimitPerIPDurationMinutesLocal"
              @input="onMessageLimitPerIPDurationMinutesInput($event.target.value)"
              class="input input-bordered"
              :placeholder="t('aiChatAgentSetup.advanced.limitDurationMinutesPlaceholder', 'e.g. 3')"
              :disabled="limitMessagesPerIPEnabledLocal !== true"
              aria-label="IP limit duration in minutes"
            />
          </div>
          <div v-if="props.whatsappChannelSelected === true" class="border border-base-300 rounded-box p-4">
            <h3 class="text-base font-semibold mb-2">{{ t('aiChatAgentSetup.advanced.whatsappPhoneLimitTitle', 'WhatsApp phone number limit') }}</h3>
            <p class="text-xs text-gray-500 mb-3">{{ t('aiChatAgentSetup.advanced.whatsappPhoneLimitHint', 'Applies the same limiter by WhatsApp sender phone number when the IP limit is enabled.') }}</p>
            <div class="form-control">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitMessagesPerPhoneNumber', 'Limit messages per phone number') }}</span>
              </label>
              <input
                type="number"
                min="0"
                :value="messageLimitPerPhoneNumberLocal"
                @input="onMessageLimitPerPhoneNumberInput($event.target.value)"
                class="input input-bordered"
                :placeholder="t('aiChatAgentSetup.advanced.unlimitedPlaceholder', '0 for unlimited')"
                :disabled="limitMessagesPerIPEnabledLocal !== true"
                aria-label="Limit messages per phone number"
              />
            </div>
            <div class="form-control mt-3">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.limitMessagesPerPhoneNumberDurationMinutes', 'Phone number limit duration in minutes') }}</span>
              </label>
              <input
                type="number"
                min="1"
                :value="messageLimitPerPhoneNumberDurationMinutesLocal"
                @input="onMessageLimitPerPhoneNumberDurationMinutesInput($event.target.value)"
                class="input input-bordered"
                :placeholder="t('aiChatAgentSetup.advanced.limitDurationMinutesPlaceholder', 'e.g. 3')"
                :disabled="limitMessagesPerIPEnabledLocal !== true"
                aria-label="Phone number limit duration in minutes"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useI18n } from '@/i18n'
import { Icon } from '@iconify/vue/dist/offline'
import axios from 'axios'
import { globalState } from '@/globalState.js'

const defaultLimitReachedMessage = 'You have reached the maximum messages for this conversation. Start a new chat to continue.'

const props = defineProps({
  languages: { type: Array, required: true },
  inappropriateContentMessage: { type: Object, required: true },
  enablePIIRedaction: { type: Boolean, required: false, default: false },
  redactEmails: { type: Boolean, required: false, default: true },
  redactPhoneNumbers: { type: Boolean, required: false, default: false },
  verifyLinks: { type: Boolean, required: false, default: true },
  verifyAnswerEnabled: { type: Boolean, required: false, default: false },
  customBlockRegex: { type: Array, required: false, default: () => [] },
  limitReachedMessage: { type: String, required: false, default: defaultLimitReachedMessage },
  messageLimitPerConversation: { type: [Number, String], required: false, default: 0 },
  messageLimitPerIP: { type: [Number, String], required: false, default: 0 },
  messageLimitPerIPDurationMinutes: { type: [Number, String], required: false, default: 3 },
  messageLimitPerPhoneNumber: { type: [Number, String], required: false, default: 0 },
  messageLimitPerPhoneNumberDurationMinutes: { type: [Number, String], required: false, default: 3 },
  limitMessagesPerConversationEnabled: { type: Boolean, required: false, default: false },
  limitMessagesPerIPEnabled: { type: Boolean, required: false, default: false },
  whatsappChannelSelected: { type: Boolean, required: false, default: false },
  disableVerifyAnswerToggle: { type: Boolean, required: false, default: false },
  authenticatorEnabled: { type: Boolean, required: false, default: false },
  authenticatorProvider: { type: String, required: false, default: 'google' },
  authenticatorWelcomeMessage: { type: String, required: false, default: 'Welcome! Please sign in at {{authURL}} to continue.' },
  authenticatorRetryMessage: { type: String, required: false, default: 'You must login via {{authURL}} to continue.' },
  authenticatorSuccessMessage: { type: String, required: false, default: 'You are signed in. Lets talk.' },
  authenticatorRunAiToolOnLogin: { type: Boolean, required: false, default: false }
})

const emit = defineEmits([
  'update:inappropriateContentMessage',
  'update:enablePIIRedaction',
  'update:redactEmails',
  'update:redactPhoneNumbers',
  'update:verifyLinks',
  'update:verifyAnswerEnabled',
  'update:customBlockRegex',
  'update:limitReachedMessage',
  'update:messageLimitPerConversation',
  'update:messageLimitPerIP',
  'update:messageLimitPerIPDurationMinutes',
  'update:messageLimitPerPhoneNumber',
  'update:messageLimitPerPhoneNumberDurationMinutes',
  'update:limitMessagesPerConversationEnabled',
  'update:limitMessagesPerIPEnabled',
  'update:authenticatorEnabled',
  'update:authenticatorProvider',
  'update:authenticatorWelcomeMessage',
  'update:authenticatorRetryMessage',
  'update:authenticatorSuccessMessage',
  'update:authenticatorRunAiToolOnLogin'
])

const { t } = useI18n()

const selectedTab = ref('moderation')
const asideRef = ref(null)
const contentMinHeight = ref('16rem')
const selectedModerationLang = ref('en')
const authenticatorWelcomeDefault = 'Welcome! Please sign in at {{authURL}} to continue.'
const authenticatorRetryDefault = 'You must login via {{authURL}} to continue.'
const authenticatorSuccessDefault = 'You are signed in. Lets talk.'
const authProviderStatuses = ref({})
const authStatusLoading = ref(false)
const forcePIIRedaction = ref(false)
const isStandardSubscription = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : ''
  return subscription === 'standard'
})

function recalcContentMinHeight() {
  const asideEl = asideRef.value
  if (asideEl !== null && asideEl !== undefined) {
    const h = asideEl.offsetHeight
    contentMinHeight.value = h > 0 ? h + 'px' : '16rem'
  }
}

function handleResize() {
  recalcContentMinHeight()
}

async function loadAuthProviderStatuses() {
  authStatusLoading.value = true
  try {
    const endpoint = globalState.apiEndpoint
    const res = await axios.get(`${endpoint}/agents/auth/providers/status`)
    if (res !== undefined && res !== null && typeof res.data === 'object') {
      authProviderStatuses.value = res.data
    } else {
      authProviderStatuses.value = {}
    }
  } catch (e) {
    authProviderStatuses.value = {}
  } finally {
    authStatusLoading.value = false
  }
}
async function loadRuntimeConfig() {
  try {
    const endpoint = globalState.apiEndpoint
    const res = await axios.get(`${endpoint}/api/config/runtime`)
    forcePIIRedaction.value = res?.data?.forcePIIRedaction === true
  } catch (e) {
    forcePIIRedaction.value = false
  }
}

onMounted(async () => {
  await nextTick()
  recalcContentMinHeight()
  window.addEventListener('resize', handleResize)
  await loadRuntimeConfig()
  await loadAuthProviderStatuses()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

const languages = computed(() => Array.isArray(props.languages) === true ? props.languages : [])

const currentModerationMessage = computed({
  get: () => props.inappropriateContentMessage[selectedModerationLang.value] !== undefined ? props.inappropriateContentMessage[selectedModerationLang.value] : '',
  set: (value) => {
    const updated = {}
    for (const k in props.inappropriateContentMessage) {
      updated[k] = props.inappropriateContentMessage[k]
    }
    updated[selectedModerationLang.value] = value
    emit('update:inappropriateContentMessage', updated)
  }
})

const moderationChars = computed(() => currentModerationMessage.value !== undefined ? currentModerationMessage.value.length : 0)

const enablePIIRedactionLocal = computed(() => props.enablePIIRedaction === true)
const effectiveEnablePIIRedaction = computed(() => {
  if (forcePIIRedaction.value === true) return true
  if (isStandardSubscription.value === true) return true
  return enablePIIRedactionLocal.value === true
})
const disablePIIRedactionToggle = computed(() => forcePIIRedaction.value === true || isStandardSubscription.value === true)
const piiLockedMessage = computed(() => {
  if (forcePIIRedaction.value === true) return t('aiChatAgentSetup.advanced.redactLockedCloud', 'Cloud version keeps PII redaction enabled and locked.')
  if (isStandardSubscription.value === true) return 'Standard plan keeps PII redaction enabled and locked.'
  return ''
})
const verifyLinksLocal = computed(() => props.verifyLinks === true)
const verifyAnswerEnabledLocal = computed(() => props.verifyAnswerEnabled === true)
const authenticatorEnabledLocal = computed({
  get: () => props.authenticatorEnabled === true,
  set: (value) => emit('update:authenticatorEnabled', value === true)
})
const authenticatorProviderLocal = computed({
  get: () => {
    const val = typeof props.authenticatorProvider === 'string' ? props.authenticatorProvider : ''
    if (val === undefined || val === null || val === '') return 'google'
    return val
  },
  set: (value) => emit('update:authenticatorProvider', typeof value === 'string' && value !== '' ? value : 'google')
})
const authenticatorWelcomeMessageModel = computed({
  get: () => typeof props.authenticatorWelcomeMessage === 'string' && props.authenticatorWelcomeMessage.trim().length > 0 ? props.authenticatorWelcomeMessage : authenticatorWelcomeDefault,
  set: (value) => emit('update:authenticatorWelcomeMessage', typeof value === 'string' ? value : '')
})
const authenticatorRetryMessageModel = computed({
  get: () => typeof props.authenticatorRetryMessage === 'string' && props.authenticatorRetryMessage.trim().length > 0 ? props.authenticatorRetryMessage : authenticatorRetryDefault,
  set: (value) => emit('update:authenticatorRetryMessage', typeof value === 'string' ? value : '')
})
const authenticatorSuccessMessageModel = computed({
  get: () => typeof props.authenticatorSuccessMessage === 'string' && props.authenticatorSuccessMessage.trim().length > 0 ? props.authenticatorSuccessMessage : authenticatorSuccessDefault,
  set: (value) => emit('update:authenticatorSuccessMessage', typeof value === 'string' ? value : '')
})
const authenticatorRunAiToolOnLoginLocal = computed({
  get: () => props.authenticatorRunAiToolOnLogin === true,
  set: (value) => emit('update:authenticatorRunAiToolOnLogin', value === true)
})
const selectedAuthProviderStatus = computed(() => {
  const provider = authenticatorProviderLocal.value
  const statuses = authProviderStatuses.value
  if (statuses !== undefined && statuses !== null && typeof statuses === 'object') {
    const status = statuses[provider]
    if (status !== undefined) return status
  }
  return null
})
const authNotConfiguredText = computed(() => {
  const status = selectedAuthProviderStatus.value
  if (status === null) return ''
  if (status.configured === true) return ''
  const missing = Array.isArray(status.missingEnvs) === true ? status.missingEnvs : []
  const missingText = missing.length > 0 ? missing.join(', ') : ''
  if (authenticatorProviderLocal.value === 'aad') {
    const required = ['azureAdClientId', 'azureAdClientSecret', 'azureAdTenantId', 'azureAdRedirectUrl']
    const listText = missingText !== '' ? missingText : required.join(', ')
    return `Add ${listText} to the environment and talk with your support team for configuring.`
  }
  if (missingText !== '') {
    return `Add ${missingText} to the environment and talk with your support team for configuring.`
  }
  return 'Talk with your support team for configuring this provider.'
})
const limitMessagesPerConversationEnabledLocal = computed(() => props.limitMessagesPerConversationEnabled === true)
const limitMessagesPerIPEnabledLocal = computed(() => props.limitMessagesPerIPEnabled === true)

function onEnablePIIRedactionChange(value) {
  if (disablePIIRedactionToggle.value === true) {
    emit('update:enablePIIRedaction', true)
    return
  }
  emit('update:enablePIIRedaction', value === true)
}

function onRedactPhoneNumbersChange(value) {
  emit('update:redactPhoneNumbers', value === true)
}

function onRedactEmailsChange(value) {
  emit('update:redactEmails', value === true)
}

watch([forcePIIRedaction, isStandardSubscription, () => props.enablePIIRedaction], ([forceValue, standardValue, enableValue]) => {
  if ((forceValue === true || standardValue === true) && enableValue !== true) {
    emit('update:enablePIIRedaction', true)
  }
}, { immediate: true })

function onVerifyLinksChange(value) {
  emit('update:verifyLinks', value === true)
}

function onVerifyAnswerEnabledChange(value) {
  emit('update:verifyAnswerEnabled', value === true)
}

function onAuthenticatorEnabledChange(value) {
  authenticatorEnabledLocal.value = value === true
}

function onAuthenticatorProviderChange(value) {
  const val = typeof value === 'string' ? value : 'google'
  authenticatorProviderLocal.value = val
}

function onAuthenticatorRunToolChange(value) {
  authenticatorRunAiToolOnLoginLocal.value = value === true
}

const customBlockRegexText = computed(() => {
  const arr = Array.isArray(props.customBlockRegex) === true ? props.customBlockRegex : []
  return arr.join('\n')
})

function onCustomBlockRegexInput(value) {
  if (typeof value !== 'string') {
    emit('update:customBlockRegex', [])
    return
  }
  const parts = value.split('\n').map(x => x.trim()).filter(x => x.length > 0)
  emit('update:customBlockRegex', parts)
}

const limitReachedMessageModel = computed({
  get: () => typeof props.limitReachedMessage === 'string' && props.limitReachedMessage.trim().length > 0 ? props.limitReachedMessage : defaultLimitReachedMessage,
  set: (v) => emit('update:limitReachedMessage', typeof v === 'string' ? v : '')
})

const messageLimitPerConversationLocal = computed(() => {
  const val = props.messageLimitPerConversation
  if (val === undefined || val === null) return 0
  const n = Number(val)
  return Number.isFinite(n) === true && n >= 0 ? n : 0
})

const messageLimitPerIPLocal = computed(() => {
  const val = props.messageLimitPerIP
  if (val === undefined || val === null) return 0
  const n = Number(val)
  return Number.isFinite(n) === true && n >= 0 ? n : 0
})

const messageLimitPerIPDurationMinutesLocal = computed(() => {
  const val = props.messageLimitPerIPDurationMinutes
  if (val === undefined || val === null) return 3
  const n = Number(val)
  return Number.isFinite(n) === true && n >= 1 ? n : 3
})

const messageLimitPerPhoneNumberLocal = computed(() => {
  const val = props.messageLimitPerPhoneNumber
  if (val === undefined || val === null) return 0
  const n = Number(val)
  return Number.isFinite(n) === true && n >= 0 ? n : 0
})

const messageLimitPerPhoneNumberDurationMinutesLocal = computed(() => {
  const val = props.messageLimitPerPhoneNumberDurationMinutes
  if (val === undefined || val === null) return 3
  const n = Number(val)
  return Number.isFinite(n) === true && n >= 1 ? n : 3
})

function onMessageLimitPerConversationInput(value) {
  const n = Number(value)
  const v = Number.isFinite(n) === true && n >= 0 ? n : 0
  emit('update:messageLimitPerConversation', v)
}

function onMessageLimitPerIPInput(value) {
  const n = Number(value)
  const v = Number.isFinite(n) === true && n >= 0 ? n : 0
  emit('update:messageLimitPerIP', v)
}

function onMessageLimitPerIPDurationMinutesInput(value) {
  const n = Number(value)
  const v = Number.isFinite(n) === true && n >= 1 ? n : 3
  emit('update:messageLimitPerIPDurationMinutes', v)
}

function onMessageLimitPerPhoneNumberInput(value) {
  const n = Number(value)
  const v = Number.isFinite(n) === true && n >= 0 ? n : 0
  emit('update:messageLimitPerPhoneNumber', v)
}

function onMessageLimitPerPhoneNumberDurationMinutesInput(value) {
  const n = Number(value)
  const v = Number.isFinite(n) === true && n >= 1 ? n : 3
  emit('update:messageLimitPerPhoneNumberDurationMinutes', v)
}

function onLimitMessagesPerConversationEnabledChange(value) {
  const v = value === true
  emit('update:limitMessagesPerConversationEnabled', v)
  if (v !== true) {
    emit('update:messageLimitPerConversation', 0)
  }
}

function onLimitMessagesPerIPEnabledChange(value) {
  const v = value === true
  emit('update:limitMessagesPerIPEnabled', v)
  if (v !== true) {
    emit('update:messageLimitPerIP', 0)
    emit('update:messageLimitPerPhoneNumber', 0)
  }
}
</script>

